import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import { UserCheck } from 'lucide-react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from '../components/GlassCard';
import { DEFAULT_STUDENT_MAJORS, fetchStudentMajors } from '../constants/majors';

const MAJORS = DEFAULT_STUDENT_MAJORS;

const ProfileSetup = () => {
  const [formData, setFormData] = useState({
    prefix: '',
    first_name: '',
    last_name: '',
    department: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [majorOptions, setMajorOptions] = useState<string[]>(MAJORS);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (!user.id || (user.role !== 'SUPERVISOR' && user.role !== 'CO_TEACHER')) {
      navigate('/login');
      return;
    }

    const fetchCurrentProfile = async () => {
      try {
        const endpoint = user.role === 'SUPERVISOR' ? '/supervisors/me' : '/co-teachers/me';
        const res = await api.get(endpoint);
        setFormData({
          prefix: res.data.prefix || '',
          first_name: res.data.first_name || '',
          last_name: res.data.last_name || '',
          department: res.data.department || res.data.position || '',
        });

        let studentMajors: string[] = [];
        let depts: string[] = [];
        try {
          const majorsRes = await api.get('/supervisors/student-majors');
          studentMajors = majorsRes.data?.studentMajors || [];
          depts = majorsRes.data?.supervisorDepartments || [];
        } catch (_) {}

        try {
          const dynamicMajors = await fetchStudentMajors();
          const combined = Array.from(new Set([...DEFAULT_STUDENT_MAJORS, ...dynamicMajors, ...studentMajors, ...depts]))
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b, 'th'));
          setMajorOptions(combined);
        } catch (_) {
          const combined = Array.from(new Set([...DEFAULT_STUDENT_MAJORS, ...studentMajors, ...depts]))
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b, 'th'));
          setMajorOptions(combined);
        }
      } catch (err) {
        console.error('Failed to fetch profile', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCurrentProfile();
  }, [user.id, user.role, navigate]);

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || (user.role === 'SUPERVISOR' && !formData.department)) {
      return setError('กรุณากรอกข้อมูลให้ครบถ้วน');
    }

    try {
      const endpoint = user.role === 'SUPERVISOR' ? '/supervisors/me' : '/co-teachers/me';
      await api.put(endpoint, {
        prefix: formData.prefix,
        first_name: formData.first_name,
        last_name: formData.last_name,
        department: formData.department,
      });
      alert('ยืนยันข้อมูลส่วนตัวเรียบร้อยแล้ว');
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
      setError('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    }
  };

  if (loading) {
    return (
      <Box sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: 6,
        background:
          'radial-gradient(circle at 50% 25%, rgba(30, 64, 175, 0.08) 0%, rgba(248, 250, 252, 0) 70%)',
      }}
    >
      <Container maxWidth="sm">
        <GlassCard sx={{ p: { xs: 3, sm: 5 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 54,
                height: 54,
                mx: 'auto',
                mb: 1.5,
                bgcolor: 'primary.main',
                boxShadow: '0 6px 14px rgba(30, 64, 175, 0.25)',
              }}
            >
              <UserCheck size={28} />
            </Avatar>
            <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700 }}>
              ยืนยันข้อมูลส่วนตัว (Profile Verification)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              ยินดีต้อนรับคุณ {user.username} • กรุณาตรวจสอบข้อมูลเพื่อเริ่มต้นใช้งาน
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="คำนำหน้าชื่อ (Prefix)"
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
                placeholder="เช่น ผศ.ดร., อาจารย์, นาย, นาง"
              />
              <TextField
                fullWidth
                label="ชื่อ (First Name)"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
              <TextField
                fullWidth
                label="นามสกุล (Last Name)"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />

              {user.role === 'SUPERVISOR' ? (
                <TextField
                  fullWidth
                  select
                  label="สาขาวิชา"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="">
                    <em>เลือกสาขาวิชา</em>
                  </MenuItem>
                  {majorOptions.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  label="ตำแหน่ง (Position)"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  placeholder="เช่น ครูวิทยฐานะชำนาญการ"
                />
              )}

              <Button type="submit" fullWidth variant="contained" size="large" sx={{ mt: 1 }}>
                ยืนยันข้อมูลและเข้าสู่ระบบ
              </Button>
            </Box>
          </form>
        </GlassCard>
      </Container>
    </Box>
  );
};

export default ProfileSetup;
